import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useLoader } from "@/hooks/LoaderProvider";
import {
  updateStep,
  setCurrentStep,
  selectStep1Data,
} from "@/store/redux/slices/kyc/kyc_setup_slice";
import {
  uploadFile,
  selectFileUploadLoading,
} from "@/store/redux/slices/user/file_upload_slice";
import { createOrganization } from "@/store/redux/slices/kyc/kyc_setup_slice"; // Import createOrganization
import { getUserId } from "@/utils/token_functions";

import Button from "@/components/common/Button";
import Input from "@/components/common/inputs/Input";
import CountryFlag from "@/components/common/Country_Flag";
import Success from "@/components/common/alerts/Success";
import ProfilePicker from "@/components/specific/Profile_Picker";
import { ArrowRightOutlined } from "@/config/antd";
import Copyright from "@/components/layout/Footer/Copyright";
import WebIcon from "@/components/common/Web_Icons";
import LocationInput from "@/components/common/inputs/Location_Input";

const AccountSetupStep1 = () => {
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Get data from both slices
  const step1Data = useSelector(selectStep1Data);
  const isFileUploading = useSelector(selectFileUploadLoading);

  const [isSuccessVisible, setIsSuccessVisible] = useState(false);
  const [isSecondSuccessVisible, setIsSecondSuccessVisible] = useState(false);

  // Updated state variables to match backend field names
  const [name, setName] = useState(step1Data.name || "");
  const [type, setType] = useState(step1Data.type || "");
  const [role, setRole] = useState(step1Data.role || "");
  const [address, setAddress] = useState(step1Data.address || "");
  const [email, setEmail] = useState(step1Data.email || "");
  const [phone, setPhone] = useState(step1Data.phone || "");
  const [profileImage, setProfileImage] = useState(null);
  const [profilePreview, setProfilePreview] = useState(null);

  useEffect(() => {
    if (step1Data.ownerProfileImage) {
      setProfileImage(step1Data.ownerProfileImage);
      setProfilePreview(step1Data.ownerProfileImage); // Use same base64 for preview
    }
  }, [step1Data.ownerProfileImage]);

  const isFormFilled = name && type && address && email && phone;

  const businessTypeOptions = [
    { value: "LLC", label: "Limited Liability Company" },
    { value: "CORPORATION", label: "Corporation" },
    { value: "PARTNERSHIP", label: "Partnership" },
    // Add more options as needed
  ];

  const roleOptions = [
    { value: "Super Admin", label: "Super Admin" },
    { value: "Campaign Manager", label: "Campaign Manager" },
    { value: "Event Admin", label: "Event Admin" },
    { value: "Finance Admin", label: "Finance Admin" },
    { value: "Communication Manager", label: "Communication Manager" },
    { value: "Canvasser", label: "Canvasser" },
    { value: "Volunteer", label: "Volunteer" },
  ];

  const handleProfileImageChange = async (file) => {
    if (file) {
      try {
        // Only handle preview functionality
        const reader = new FileReader();
        reader.onload = () => {
          const base64String = reader.result;
          setProfilePreview(base64String);
          setProfileImage(file); // Store the actual file for later upload

          // Store preview in Redux for temporary state
          dispatch(
            updateStep({
              step: "step1",
              data: {
                ownerProfileImage: base64String,
              },
            })
          );
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error("Error processing file:", error);
        setProfilePreview(null);
      }
    }
  };

  const handleProceed = async () => {
    showLoader();
    try {
      await dispatch(
        updateStep({
          step: "step1",
          data: {
            name,
            type,
            address,
            email,
            phone,
            role,
            ownerProfileImage: profilePreview,
          },
        })
      );
      setIsSuccessVisible(true);
    } catch (error) {
      console.error("Error saving data:", error);
    } finally {
      hideLoader();
    }
  };

  const handleCloseSuccess = () => {
    setIsSuccessVisible(false);
  };

  // Add new state to track which path user chose
  const [isSaveAndProceedLater, setIsSaveAndProceedLater] = useState(false);

  const handleFirstSuccessProceed = () => {
    showLoader();
    setTimeout(() => {
      hideLoader();
      showLoader();
      dispatch(setCurrentStep(2));
      navigate("/account-setup-step-2");
      hideLoader();
    }, 2000);
  };

  const handleSaveAndProceedLater = async () => {
    setIsSuccessVisible(false);
    setIsSaveAndProceedLater(true);

    showLoader();
    try {
      const userID = getUserId();

      // Only upload the image if one was selected
      let imageUrl = null;
      if (profileImage instanceof File) {
        const uploadResult = await dispatch(
          uploadFile({
            file: profileImage,
            fileType: "ownerProfileImage",
          })
        ).unwrap();
        imageUrl = uploadResult.fileUrl;
      }

      await dispatch(
        createOrganization({
          name,
          type,
          address,
          email,
          phone,
          ownerProfileImage: imageUrl, // Use the uploaded URL
          userID,
          role,
        })
      );
      setIsSecondSuccessVisible(true);
    } catch (error) {
      console.error("Error creating organization:", error);
    } finally {
      hideLoader();
    }
  };

  const handleCloseSecondSuccess = () => {
    setIsSecondSuccessVisible(false);
  };

  const handleContinue = () => {
    showLoader();
    setTimeout(() => {
      setIsSecondSuccessVisible(false);
      // Check which path to take
      if (isSaveAndProceedLater) {
        navigate("/signin"); // Redirect to signin if user chose "save and proceed later"
      } else {
        dispatch(setCurrentStep(2));
        navigate("/account-setup-step-2"); // Continue to next step if user chose "proceed"
      }
      hideLoader();
    }, 2000);
  };

  return (
    <div
      className="flex flex-col min-h-screen text-left bg-white rounded-lg lg:border lg:p-5"
      data-testid="account-setup-step1">
      {/* Top section */}
      {/* <div className="flex items-center justify-between p-4">
        <div className="flex items-center logo-container">
          <img
            src={Logo}
            alt="Veegil Logo"
            className="logo-image w-[53px] h-[44px]"
          />
          <span className="text-[24px] text-primary font-roboto-slab font-normal">
            Veegil
          </span>
        </div>
        <div className="flex items-center">
          <Avatar size={32} icon={<UserOutlined />} className="mr-2" />
          <span className="text-bold text-primary">Hello, { }</span>
        </div>
      </div> */}

      {/* Middle section */}
      <div className="flex flex-col w-full px-4 mt-4 mb-8">
        <div className="hidden md:block">
          <h1 className="mb-2 text-3xl font-bold text-left whitespace-nowrap text-primary">
            Onboard your Organization
          </h1>
          <p className="mb-4 text-left text-small text-darkGrey">
            Enter your organization&apos;s information below to continue with
            your account setup.
          </p>
        </div>
        <div className="flex flex-col gap-10 md:flex-row">
          <div className="flex-1 rounded-lg lg:border lg:p-5">
            <h2 className="mb-4 text-left text-black heading-medium">
              Basic Information
            </h2>
            {/* <div className="flex justify-start mb-6">
              <Upload
                name="avatar"
                // listType="picture-circle"
                className="avatar-uploader"
                showUploadList={false}
              >
                <div className="flex items-center justify-center w-20 h-20 rounded-full bg-lightGray">
                  <CameraOutlined
                    style={{ fontSize: "30px", color: "white" }}
                  />
                </div>
              </Upload>
            </div> */}

            <ProfilePicker
              currentImage={profilePreview} // Use preview URL instead of file URL
              onImageChange={handleProfileImageChange}
              className="mb-6"
              bgColor="lightGray"
              isLoading={isFileUploading}
            />

            <div className="mb-4">
              <Input
                label="ORGANIZATION NAME"
                type="text"
                data-testid="organization-name-input"
                placeholder="Enter company name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="mb-4">
              <Input
                label="ORGANIZATION TYPE"
                type="select"
                data-testid="organization-type-input"
                placeholder="Select"
                options={businessTypeOptions}
                value={type}
                onChange={(e) => setType(e.target.value)}
              />
            </div>
            <div className="mb-4">
              <Input
                label="ROLE IN THE ORGANIZATION"
                placeholder="Select"
                type="select"
                options={roleOptions}
                value={role}
                onChange={(e) => setRole(e.target.value)}
              />
            </div>
            <h2 className="mt-6 mb-4 text-left text-black heading-medium">
              Location Information
            </h2>
            <div className="mb-4">
              <LocationInput
                label="ORGANIZATION ADDRESS"
                placeholder="Enter organization address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </div>

            <div className="mb-4">
              <Input
                label="EMAIL"
                type="email"
                data-testid="organization-email-input"
                placeholder="email@domain.com"
                icon={<WebIcon icon="Message" size="small" />}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="mb-4">
              <Input
                label="PHONE"
                type="tel"
                data-testid="organization-phone-input"
                placeholder="Phone Number"
                icon={<CountryFlag />}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>

            <Button
              text="Proceed"
              data-testid="step1-proceed-button"
              icon={<ArrowRightOutlined />}
              onClick={handleProceed}
              isActive={!!isFormFilled} // Convert to boolean
              color="darkBlue"
              className={`w-full mt-6 ${
                isFormFilled
                  ? "bg-primary text-white"
                  : "bg-darkBlue text-primary"
              }`}
            />
          </div>
        </div>

        <div className="mt-auto">
          <Copyright />
        </div>
      </div>

      <Success
        isVisible={isSuccessVisible}
        onClose={handleCloseSuccess}
        header="KYC Step Saved"
        paragraph="Your kyc step has been saved. You can proceed below or save and continue later."
        buttonText="Proceed"
        showCancelButton={true}
        cancelButtonText="I want to save and proceed later"
        onButtonClick={handleFirstSuccessProceed}
        onCancelClick={handleSaveAndProceedLater}
      />

      <Success
        isVisible={isSecondSuccessVisible}
        onClose={handleCloseSecondSuccess}
        header="KYC Step Saved"
        paragraph={
          isSaveAndProceedLater
            ? "You can proceed with your KYC at any time when you are ready but remember, your account will still be limited until approval."
            : "Your KYC step has been saved. Click continue to proceed to the next step."
        }
        buttonText="Continue"
        showCancelButton={false}
        onButtonClick={handleContinue}
      />
    </div>
  );
};

export default AccountSetupStep1;
